import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import QuizDialog from "@/components/QuizDialog";
import { ARCHETYPE_META } from "@/data/quiz";

/**
 * Landing v2 — Apple Pro 70% / Linear 30%.
 *
 * Filosofía: cada sección es una escena visual, no un bloque informativo.
 * Sistema atmosférico de 3 capas:
 *   1. Fondo base #050505 (negro premium)
 *   2. Capa atmosférica (glows teal/azul muy difuminados, grain sutil)
 *   3. Contenido tipográfico protagonista
 *
 * Fase 1: Hero + Problema.
 */
export default function LandingV2() {
  const [quizOpen, setQuizOpen] = useState(false);

  return (
    <div
      data-testid="landing-v2"
      className="bg-[#050505] text-white min-h-screen relative overflow-x-hidden"
    >
      {/* Global grain overlay */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-[1] opacity-[0.035]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.85) 1px, transparent 0)",
          backgroundSize: "3px 3px",
        }}
      />

      {/* Minimal nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/30 border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="text-sm font-semibold tracking-[0.32em]">
            PLAY YOUR GAME™
          </div>
          <div className="hidden md:flex items-center gap-10 text-[10px] uppercase tracking-[0.3em] font-medium text-white/45">
            <a href="#problema" className="hover:text-white transition-colors duration-300">
              Problema
            </a>
            <a href="#evolucion" className="hover:text-white transition-colors duration-300">
              Evolución
            </a>
            <a href="#avatares" className="hover:text-white transition-colors duration-300">
              Sistema
            </a>
          </div>
          <Link
            to="/acceder"
            data-testid="nav-cta-btn"
            className="inline-flex items-center gap-2 bg-white/[0.06] hover:bg-white/[0.10] border border-white/10 hover:border-white/20 text-white text-[10px] sm:text-[10px] tracking-[0.18em] sm:tracking-[0.22em] uppercase font-semibold px-3 sm:px-4 py-2 sm:py-2.5 rounded-sm transition-all duration-300"
          >
            <span className="hidden sm:inline">Ingresar a mi perfil</span>
            <span className="sm:hidden">Mi perfil</span>
            <ArrowRight size={11} />
          </Link>
        </div>
      </nav>

      {/* ───── ESCENA 1 · HERO ───── */}
      <HeroScene onCta={() => setQuizOpen(true)} />

      {/* ───── ESCENA 2 · PROBLEMA ───── */}
      <ProblemaScene />

      {/* ───── ESCENA 3 · VENTAJA ESTRATÉGICA ───── */}
      <VentajaScene />

      {/* ───── ESCENA 4 · EVOLUCIÓN (LIGHT BAND) ───── */}
      <EvolucionScene onCta={() => setQuizOpen(true)} />

      {/* ───── ESCENA 5 · AVATARES ───── */}
      <AvataresScene />

      {/* ───── ESCENA 6 · DESCUBRIMIENTOS (LIGHT) ───── */}
      <DescubrimientosScene />

      {/* ───── ESCENA 7 · COACH ESTRATÉGICO ───── */}
      <CoachScene onCta={() => setQuizOpen(true)} />

      {/* ───── ESCENA 8 · EMPRESAS ───── */}
      <EmpresasScene />

      {/* ───── ESCENA 9 · CTA FINAL ───── */}
      <CtaFinalScene onCta={() => setQuizOpen(true)} />

      {/* ───── ESCENA 10 · FOOTER ───── */}
      <FooterScene />

      <QuizDialog open={quizOpen} onOpenChange={setQuizOpen} />
    </div>
  );
}

// ───────────────────────── ESCENA 1 · HERO ───────────────────────── //
function HeroScene({ onCta }) {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center px-6 pt-32 pb-24 overflow-hidden"
    >
      {/* Atmospheric layers */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-[-20%] left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(41,188,155,0.08) 0%, rgba(41,188,155,0) 55%)",
          filter: "blur(60px)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-[-30%] right-[-10%] w-[900px] h-[900px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(26,35,48,0.6) 0%, rgba(26,35,48,0) 60%)",
          filter: "blur(80px)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute top-1/3 left-[-15%] w-[700px] h-[700px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(255,255,255,0.025) 0%, transparent 60%)",
          filter: "blur(70px)",
        }}
      />

      {/* Content — left aligned for editorial feel, asymmetric */}
      <div className="relative w-full max-w-[1180px] mx-auto">
        <div
          data-testid="hero-eyebrow"
          className="text-[11px] tracking-[0.4em] uppercase text-[#29BC9B] font-semibold mb-10"
          style={{ animation: "fadeUp 900ms 100ms ease-out both" }}
        >
          Evaluación estratégica · Play Your Game™
        </div>

        <h1
          data-testid="hero-title"
          className="font-semibold leading-[0.95] tracking-[-0.03em] text-[44px] sm:text-[68px] md:text-[88px] lg:text-[112px] max-w-[1080px]"
          style={{ animation: "fadeUp 1000ms 200ms ease-out both" }}
        >
          Juega mejor
          <br />
          <span className="text-white/55">bajo presión.</span>
        </h1>

        <p
          data-testid="hero-subtitle"
          className="mt-10 text-[17px] sm:text-[19px] leading-relaxed text-white/55 max-w-[640px]"
          style={{ animation: "fadeUp 1000ms 380ms ease-out both" }}
        >
          Descubre tu ventaja estratégica, identifica tu modo sombra y aprende
          a tomar mejores decisiones cuando más importa.
        </p>

        <div
          className="mt-14 flex flex-wrap items-center gap-6"
          style={{ animation: "fadeUp 1000ms 560ms ease-out both" }}
        >
          <button
            onClick={onCta}
            data-testid="hero-cta-btn"
            className="group inline-flex items-center gap-3 bg-[#29BC9B] hover:bg-[#22a989] text-[#050505] text-[12px] tracking-[0.22em] uppercase font-bold px-7 py-4 rounded-sm transition-all duration-300"
          >
            Descubre cómo juegas bajo presión
            <ArrowRight
              size={15}
              className="group-hover:translate-x-1 transition-transform"
            />
          </button>
          <span className="text-[11px] tracking-[0.22em] uppercase text-white/30">
            5 min · gratuito · sin registro
          </span>
        </div>

        {/* Subtle scroll cue */}
        <div
          aria-hidden
          className="absolute -bottom-12 left-0 text-[10px] tracking-[0.32em] uppercase text-white/25 hidden sm:flex items-center gap-3"
          style={{ animation: "fadeUp 1200ms 900ms ease-out both" }}
        >
          <span className="inline-block w-8 h-px bg-white/20" />
          Desliza
        </div>
      </div>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}

// ───────────────────────── ESCENA 2 · PROBLEMA ───────────────────────── //
function ProblemaScene() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setVisible(true),
      { threshold: 0.25 },
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  return (
    <section
      id="problema"
      ref={ref}
      data-testid="problema-section"
      className="relative min-h-screen flex items-center px-6 py-32 md:py-40 overflow-hidden"
    >
      {/* Full-section human presence — MIRROR composition to Ventaja.
          Face anchored LEFT, text on RIGHT. Heavy chiaroscuro (modo sombra metaphor).
          Stack order (bg → fg):
            1. Base #050505
            2. Face (full-width, screen blend, anchored left, deep shadow)
            3. Right-side dark veil (reading zone for text)
            4. Atmospheric glows
            5. Horizontal connecting light beam (face ↔ text)
            6. Trajectory SVG lines (span full width)
            7. Content (z-10)
      */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden"
      >
        <img
          src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=2000&q=85"
          alt=""
          className="ly2-face-img absolute inset-0 w-full h-full object-cover"
          style={{
            filter: "grayscale(1) contrast(1.18) brightness(0.88)",
            mixBlendMode: "screen",
            opacity: 0.34,
            objectPosition: "center 32%",
            transform: "translateX(-22%) scale(1.05)",
            WebkitMaskImage:
              "radial-gradient(ellipse 40% 82% at 48% 50%, #000 24%, rgba(0,0,0,0.50) 52%, transparent 80%)",
            maskImage:
              "radial-gradient(ellipse 40% 82% at 48% 50%, #000 24%, rgba(0,0,0,0.50) 52%, transparent 80%)",
          }}
        />
        {/* Dark reading veil — fades face right-of-center; atmospheric, not divider */}
        <div
          className="ly2-reading-veil-dark absolute inset-0"
          style={{
            background:
              "linear-gradient(265deg, rgba(5,5,5,0.94) 0%, rgba(5,5,5,0.80) 45%, rgba(5,5,5,0.25) 72%, rgba(5,5,5,0) 100%)",
          }}
        />
      </div>

      {/* Soft ambient lighting from below */}
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-[-40%] left-1/2 -translate-x-1/2 w-[1400px] h-[800px] rounded-full"
        style={{
          background:
            "radial-gradient(ellipse, rgba(255,255,255,0.03) 0%, transparent 60%)",
          filter: "blur(80px)",
        }}
      />
      {/* Teal "shadow mode" highlight — falls on face area */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-1/3 left-[5%] w-[600px] h-[600px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(41,188,155,0.10) 0%, transparent 60%)",
          filter: "blur(70px)",
        }}
      />

      {/* Horizontal connecting light beam — runs across face + text */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-0 right-0 h-[1px] top-[62%]"
        style={{
          background:
            "linear-gradient(to right, transparent 0%, rgba(41,188,155,0.4) 18%, rgba(41,188,155,0.20) 50%, rgba(255,255,255,0.06) 80%, transparent 100%)",
        }}
      />

      {/* Trajectory lines spanning full width */}
      <svg
        aria-hidden
        className="pointer-events-none absolute inset-0 w-full h-full opacity-[0.10]"
        preserveAspectRatio="none"
        viewBox="0 0 1440 900"
      >
        <defs>
          <linearGradient id="lineGradDark" x1="0" x2="1" y1="0" y2="0.4">
            <stop offset="0%" stopColor="#29BC9B" stopOpacity="0" />
            <stop offset="50%" stopColor="#FFFFFF" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M-50,720 Q360,640 720,560 T1490,360" stroke="url(#lineGradDark)" strokeWidth="1.2" fill="none" />
        <path d="M-50,780 Q420,720 800,620 T1490,440" stroke="url(#lineGradDark)" strokeWidth="1" fill="none" />
      </svg>

      <div className="relative z-10 w-full max-w-[1180px] mx-auto">
        {/* Content anchored RIGHT — mirrors Ventaja's LEFT anchor */}
        <div className="ml-auto max-w-[820px] lg:pl-12">
          <div
            className={`text-[11px] tracking-[0.4em] uppercase text-white/40 font-semibold mb-12 transition-all duration-1000 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            El problema
          </div>

          <h2
            data-testid="problema-title"
            className={`font-semibold leading-[1] tracking-[-0.025em] text-[40px] sm:text-[58px] md:text-[74px] lg:text-[88px] transition-all duration-1000 delay-150 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            No fallas por
            <br />
            <span className="text-white/55">falta de capacidad.</span>
          </h2>

          <h2
            className={`mt-7 font-semibold leading-[1] tracking-[-0.025em] text-[40px] sm:text-[58px] md:text-[74px] lg:text-[88px] transition-all duration-1000 delay-300 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            Fallas por cómo
            <br />
            reaccionas{" "}
            <span className="relative inline-block">
              <span className="relative z-[1]">bajo presión.</span>
              <span
                aria-hidden
                className="absolute inset-x-0 bottom-2 h-[14px] bg-[#29BC9B]/25 -z-0"
              />
            </span>
          </h2>

          <p
            className={`mt-14 text-[15px] sm:text-[17px] leading-relaxed text-white/45 max-w-[480px] transition-all duration-1000 delay-500 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            La presión no cambia cómo juegas. Revela cómo juegas. Y muy pocos
            aprenden a reconocer ese momento.
          </p>
        </div>
      </div>
    </section>
  );
}

// ───────────────────────── Hook reutilizable ───────────────────────── //
function useReveal(threshold = 0.2) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setVisible(true),
      { threshold },
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ───────────────────────── ESCENA 3 · VENTAJA (CRISTALINO) ───────────────────────── //
function VentajaScene() {
  const [ref, visible] = useReveal(0.22);
  return (
    <section
      ref={ref}
      data-testid="ventaja-section"
      className="relative min-h-screen flex items-center px-6 py-32 md:py-40 overflow-hidden bg-[#ECECEE] text-[#0A0A0A]"
    >
      {/* Soft top edge — transition from dark Problema to crystalline Ventaja */}
      <div
        aria-hidden
        className="absolute top-0 left-0 right-0 h-px z-[5]"
        style={{
          background:
            "linear-gradient(to right, transparent, rgba(41,188,155,0.35), transparent)",
        }}
      />

      {/* Full-section human presence — the face IS the atmosphere.
          Stack order (bg → fg):
            1. Base #ECECEE
            2. Face (full-width, luminosity blend, anchored right via object-position)
            3. Silver reading veil on the left (gradient overlay)
            4. Crystalline diagonal sheets (cross over face + text → unifies)
            5. Teal prism + cool shadow glows
            6. Horizontal connecting light beam (text ↔ face)
            7. Trajectory SVG lines (span full width)
            8. Content (z-10)
      */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden"
      >
        <img
          src="https://images.unsplash.com/photo-1521119989659-a83eee488004?w=2000&q=85"
          alt=""
          className="ly2-face-img absolute inset-0 w-full h-full object-cover"
          style={{
            filter: "grayscale(1) contrast(1.05) brightness(1.08)",
            mixBlendMode: "luminosity",
            opacity: 0.30,
            objectPosition: "center 38%",
            transform: "translateX(18%) scale(1.05)",
            WebkitMaskImage:
              "radial-gradient(ellipse 45% 85% at 50% 50%, #000 26%, rgba(0,0,0,0.55) 55%, transparent 82%)",
            maskImage:
              "radial-gradient(ellipse 45% 85% at 50% 50%, #000 26%, rgba(0,0,0,0.55) 55%, transparent 82%)",
          }}
        />
        {/* Soft silver "reading veil" — fades face left-of-center so text breathes.
            Critically: NOT a sharp divider — it's a gradient atmosphere. */}
        <div
          className="ly2-reading-veil-light absolute inset-0"
          style={{
            background:
              "linear-gradient(95deg, rgba(236,236,238,0.94) 0%, rgba(236,236,238,0.80) 45%, rgba(236,236,238,0.30) 70%, rgba(236,236,238,0) 100%)",
          }}
        />
      </div>

      {/* Crystalline diagonal light sheets — pass over BOTH text and face → integrates */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(115deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 38%, rgba(255,255,255,0) 62%, rgba(255,255,255,0.42) 100%)",
        }}
      />
      {/* Cool teal prism reflection */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-1/4 right-[-10%] w-[1000px] h-[1000px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(41,188,155,0.10) 0%, transparent 55%)",
          filter: "blur(70px)",
        }}
      />
      {/* Subtle cool shadow */}
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-[-20%] left-[-10%] w-[800px] h-[800px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(110,130,150,0.18) 0%, transparent 60%)",
          filter: "blur(80px)",
        }}
      />

      {/* Horizontal connecting light beam — visually links text to face.
          Originates near the face and bleeds into the reading zone. */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-0 right-0 h-[1px] top-[58%]"
        style={{
          background:
            "linear-gradient(to right, transparent 0%, rgba(41,188,155,0.18) 30%, rgba(41,188,155,0.55) 60%, rgba(255,255,255,0.4) 85%, transparent 100%)",
        }}
      />

      {/* Abstract trajectory lines — span FULL width across text + face */}
      <svg
        aria-hidden
        className="pointer-events-none absolute inset-0 w-full h-full opacity-[0.10]"
        preserveAspectRatio="none"
        viewBox="0 0 1440 900"
      >
        <defs>
          <linearGradient id="lineGradLight" x1="0" x2="1" y1="0" y2="0.4">
            <stop offset="0%" stopColor="#0A0A0A" stopOpacity="0" />
            <stop offset="50%" stopColor="#0A0A0A" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#29BC9B" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M-50,720 Q360,640 720,560 T1490,360" stroke="url(#lineGradLight)" strokeWidth="1.2" fill="none" />
        <path d="M-50,780 Q420,720 800,620 T1490,440" stroke="url(#lineGradLight)" strokeWidth="1" fill="none" />
        <path d="M-50,840 Q480,800 880,680 T1490,520" stroke="url(#lineGradLight)" strokeWidth="0.8" fill="none" />
      </svg>

      <div className="relative z-10 w-full max-w-[1180px] mx-auto">
        <div
          className={`text-[11px] tracking-[0.4em] uppercase text-zinc-500 font-semibold mb-12 transition-all duration-1000 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          La ventaja
        </div>

        <h2
          data-testid="ventaja-title"
          className={`font-semibold leading-[1] tracking-[-0.025em] text-[40px] sm:text-[58px] md:text-[74px] lg:text-[88px] max-w-[820px] text-[#0A0A0A] transition-all duration-1000 delay-150 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          Todos tienen una
          <br />
          ventaja estratégica.
        </h2>

        <h2
          className={`mt-7 font-semibold leading-[1] tracking-[-0.025em] text-[40px] sm:text-[58px] md:text-[74px] lg:text-[88px] max-w-[820px] text-zinc-400 transition-all duration-1000 delay-300 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          Pocos aprenden a
          <br />
          dominarla.
        </h2>

        <p
          className={`mt-14 text-[15px] sm:text-[17px] leading-relaxed text-zinc-600 max-w-[480px] transition-all duration-1000 delay-500 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          No ganas por ser bueno. Ganas porque juegas en tu cancha. Cuando
          conoces cómo juegas, empiezas a jugar mejor.
        </p>
      </div>

      {/* Soft bottom edge — fluid transition into luminous Evolución */}
      <div
        aria-hidden
        className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none z-[5]"
        style={{
          background:
            "linear-gradient(to bottom, transparent, rgba(244,244,245,0.95))",
        }}
      />
    </section>
  );
}

// ───────────────────────── ESCENA 4 · EVOLUCIÓN (LIGHT) ───────────────────────── //
function EvolucionScene({ onCta }) {
  const [ref, visible] = useReveal(0.2);
  const steps = [
    {
      n: "01",
      title: "Descubre tu ventaja estratégica.",
      body: "Conoce cómo juegas naturalmente bajo presión.",
    },
    {
      n: "02",
      title: "Identifica tu modo sombra.",
      body: "Comprende cuándo tu fortaleza empieza a jugar en tu contra.",
    },
    {
      n: "03",
      title: "Entrena tu juego.",
      body: "Desarrolla nuevas respuestas para actuar mejor cuando más importa.",
    },
  ];

  return (
    <section
      id="evolucion"
      ref={ref}
      data-testid="evolucion-section"
      className="relative min-h-screen bg-[#F4F4F5] text-[#0A0A0A] py-32 md:py-40 px-6 overflow-hidden"
    >
      {/* Light atmospheric layer */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-[20%] right-[-10%] w-[800px] h-[800px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(41,188,155,0.10) 0%, transparent 60%)",
          filter: "blur(80px)",
        }}
      />
      {/* Soft top edge to smooth dark->light transition */}
      <div
        aria-hidden
        className="absolute top-0 left-0 right-0 h-px"
        style={{
          background:
            "linear-gradient(to right, transparent, rgba(41,188,155,0.30), transparent)",
        }}
      />

      <div className="relative w-full max-w-[1180px] mx-auto">
        <div
          className={`text-[11px] tracking-[0.4em] uppercase text-[#29BC9B] font-semibold mb-12 transition-all duration-1000 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          Evolución estratégica
        </div>

        <h2
          data-testid="evolucion-title"
          className={`font-semibold leading-[1] tracking-[-0.025em] text-[40px] sm:text-[58px] md:text-[68px] max-w-[860px] text-[#0A0A0A] transition-all duration-1000 delay-150 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          Tres pasos hacia la
          <br />
          <span className="text-zinc-400">madurez estratégica.</span>
        </h2>

        {/* Steps */}
        <ol className="mt-24 space-y-16 md:space-y-20">
          {steps.map((s, i) => (
            <li
              key={s.n}
              data-testid={`evolucion-step-${s.n}`}
              className={`grid grid-cols-12 gap-6 md:gap-10 items-start transition-all duration-1000 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${300 + i * 180}ms` }}
            >
              <div className="col-span-12 md:col-span-3">
                <div className="text-[64px] md:text-[88px] leading-[0.9] font-semibold tabular-nums tracking-tighter text-[#29BC9B]">
                  {s.n}
                </div>
              </div>
              <div className="col-span-12 md:col-span-9 md:pt-3 max-w-[640px]">
                <h3 className="text-[28px] sm:text-[34px] md:text-[40px] leading-[1.1] font-semibold tracking-tight text-[#0A0A0A]">
                  {s.title}
                </h3>
                <p className="mt-5 text-[16px] sm:text-[17px] text-zinc-500 leading-relaxed">
                  {s.body}
                </p>
              </div>
            </li>
          ))}
        </ol>

        {/* Outcome */}
        <div
          className={`mt-24 pt-12 border-t border-black/[0.08] flex flex-wrap items-end justify-between gap-10 transition-all duration-1000 delay-[900ms] ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="max-w-[560px]">
            <div className="text-[10px] tracking-[0.32em] uppercase text-zinc-500 font-semibold mb-3">
              Resultado
            </div>
            <p className="text-[28px] sm:text-[36px] md:text-[44px] leading-[1.05] font-semibold tracking-tight text-[#0A0A0A]">
              Juega mejor
              <br />
              <span className="text-zinc-400">bajo presión.</span>
            </p>
          </div>
          <button
            onClick={onCta}
            data-testid="evolucion-cta-btn"
            className="group inline-flex items-center gap-3 bg-[#0A0A0A] hover:bg-black text-white text-[12px] tracking-[0.22em] uppercase font-bold px-7 py-4 rounded-sm transition-all duration-300"
          >
            Descubre cómo juegas bajo presión
            <ArrowRight
              size={15}
              className="group-hover:translate-x-1 transition-transform"
            />
          </button>
        </div>
      </div>

      {/* Soft bottom edge */}
      <div
        aria-hidden
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{
          background:
            "linear-gradient(to right, transparent, rgba(41,188,155,0.30), transparent)",
        }}
      />
    </section>
  );
}

// ───────────────────────── ESCENA 5 · AVATARES ───────────────────────── //
function AvataresScene() {
  const [ref, visible] = useReveal(0.15);
  const archetypes = ["leon", "lobo", "halcon", "delfin"].map(
    (k) => ARCHETYPE_META[k],
  );

  return (
    <section
      id="avatares"
      ref={ref}
      data-testid="avatares-section"
      className="relative min-h-screen px-6 py-32 md:py-40 overflow-hidden"
    >
      {/* Atmospheric layers */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-[-15%] left-1/2 -translate-x-1/2 w-[1400px] h-[800px] rounded-full"
        style={{
          background:
            "radial-gradient(ellipse, rgba(41,188,155,0.05) 0%, transparent 60%)",
          filter: "blur(80px)",
        }}
      />

      <div className="relative w-full max-w-[1180px] mx-auto">
        <div
          className={`text-[11px] tracking-[0.4em] uppercase text-white/40 font-semibold mb-12 transition-all duration-1000 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          El sistema · Cuatro arquetipos
        </div>

        <h2
          data-testid="avatares-title"
          className={`font-semibold leading-[1] tracking-[-0.025em] text-[40px] sm:text-[58px] md:text-[74px] max-w-[940px] transition-all duration-1000 delay-150 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          Cuatro formas naturales
          <br />
          <span className="text-white/55">de jugar bajo presión.</span>
        </h2>

        <p
          className={`mt-10 text-[15px] sm:text-[17px] leading-relaxed text-white/45 max-w-[560px] transition-all duration-1000 delay-300 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          Cada arquetipo es una ventaja estratégica y, bajo presión, también un
          modo sombra que aprender a regular.
        </p>

        <div className="mt-24 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-5">
          {archetypes.map((a, i) => (
            <AvatarCard
              key={a.code}
              archetype={a}
              visible={visible}
              delay={400 + i * 130}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function AvatarCard({ archetype, visible, delay }) {
  return (
    <div
      data-testid={`avatar-card-${archetype.code}`}
      className="group relative border border-white/[0.06] hover:border-white/[0.14] bg-white/[0.015] hover:bg-white/[0.03] rounded-lg p-7 transition-all duration-500"
      style={{
        transitionDelay: `${delay}ms`,
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(28px)",
      }}
    >
      {/* Subtle accent glow on hover */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-700"
        style={{
          background: `radial-gradient(circle at 50% 0%, ${archetype.color}18 0%, transparent 60%)`,
        }}
      />
      <div className="relative">
        {/* Avatar image */}
        <div className="aspect-square w-full flex items-center justify-center mb-6">
          <img
            src={archetype.img}
            alt={archetype.name}
            className="w-[44%] h-[44%] sm:w-[78%] sm:h-[78%] object-contain transition-transform duration-700 group-hover:scale-[1.04]"
            style={{
              filter: `drop-shadow(0 0 30px ${archetype.color}30)`,
            }}
          />
        </div>

        {/* Meta */}
        <div className="space-y-2">
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/35 font-semibold tabular-nums">
            0{["leon", "lobo", "halcon", "delfin"].indexOf(archetype.code) + 1}
          </div>
          <div
            className="text-[22px] sm:text-[24px] font-semibold tracking-tight"
            style={{ color: archetype.color }}
          >
            {archetype.name}
          </div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-white/40 font-medium">
            {archetype.tagline}
          </div>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────── ESCENA 6 · DESCUBRIMIENTOS (LIGHT) ───────────────────────── //
function DescubrimientosScene() {
  const [ref, visible] = useReveal(0.15);
  const discoveries = [
    {
      photo: "https://images.unsplash.com/photo-1568602471122-7832951cc4c5?w=800&q=80",
      objectPosition: "center 25%",
      role: "Líder comercial · 30",
      quote: "Descubrí que mi problema no era la falta de disciplina. Era la impulsividad bajo presión.",
      archetype: "León",
      color: "#FF6B4A",
    },
    {
      photo: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800&q=80",
      objectPosition: "center 22%",
      role: "Gerente de producto · 30",
      quote: "Pensaba que analizar más era mi fortaleza. Descubrí que muchas veces era parálisis.",
      archetype: "Lobo",
      color: "#A78BFA",
    },
    {
      photo: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=800&q=80",
      objectPosition: "center 28%",
      role: "Director ejecutivo · 45",
      quote: "Entender cuándo mi fortaleza juega en mi contra cambió mi forma de tomar decisiones.",
      archetype: "Halcón",
      color: "#29BC9B",
    },
  ];

  return (
    <section
      ref={ref}
      data-testid="descubrimientos-section"
      className="relative min-h-screen bg-[#F4F4F5] text-[#0A0A0A] py-32 md:py-40 px-6 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: "linear-gradient(to right, transparent, rgba(41,188,155,0.30), transparent)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute top-[15%] left-1/2 -translate-x-1/2 w-[1200px] h-[700px] rounded-full"
        style={{
          background: "radial-gradient(ellipse, rgba(41,188,155,0.08) 0%, transparent 60%)",
          filter: "blur(80px)",
        }}
      />

      <div className="relative w-full max-w-[1180px] mx-auto">
        <div
          className={`text-[11px] tracking-[0.4em] uppercase text-[#29BC9B] font-semibold mb-12 transition-all duration-1000 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          Descubrimientos estratégicos
        </div>

        <h2
          data-testid="descubrimientos-title"
          className={`font-semibold leading-[1] tracking-[-0.025em] text-[40px] sm:text-[58px] md:text-[68px] max-w-[920px] transition-all duration-1000 delay-150 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          Cuando entiendes cómo juegas,
          <br />
          <span className="text-zinc-400">empiezas a jugar mejor.</span>
        </h2>

        <p
          className={`mt-10 text-[15px] sm:text-[17px] leading-relaxed text-zinc-500 max-w-[560px] transition-all duration-1000 delay-300 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          Estos son aprendizajes reales que emergen cuando una persona conoce
          su forma natural de jugar bajo presión.
        </p>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
          {discoveries.map((d, i) => (
            <article
              key={i}
              data-testid={`descubrimiento-card-${i + 1}`}
              className="group relative bg-white border border-black/[0.07] rounded-lg overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.02)] hover:shadow-[0_12px_36px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-500"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(28px)",
                transitionDelay: `${500 + i * 150}ms`,
              }}
            >
              {/* Photo */}
              <div className="relative aspect-[5/4] overflow-hidden bg-zinc-200">
                <img
                  src={d.photo}
                  alt={d.role}
                  style={{ objectPosition: d.objectPosition || "center" }}
                  className="w-full h-full object-cover grayscale-[0.15] group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-700"
                  loading="lazy"
                />
                {/* Bottom fade */}
                <div
                  aria-hidden
                  className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none"
                  style={{ background: "linear-gradient(to bottom, transparent, rgba(0,0,0,0.55))" }}
                />
                {/* Archetype pill */}
                <div
                  className="absolute bottom-4 left-4 text-[9px] tracking-[0.28em] uppercase font-bold px-2.5 py-1 rounded-sm"
                  style={{ background: d.color, color: "#050505" }}
                >
                  {d.archetype}
                </div>
              </div>

              {/* Body */}
              <div className="p-7">
                <Quote size={20} className="mb-4" style={{ color: d.color, opacity: 0.7 }} />
                <p className="text-[16px] sm:text-[17px] leading-[1.55] font-medium text-[#0A0A0A]">
                  {d.quote}
                </p>
                <div className="mt-6 pt-5 border-t border-black/[0.06] text-[10px] tracking-[0.28em] uppercase text-zinc-500 font-semibold">
                  {d.role}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      <div
        aria-hidden
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{ background: "linear-gradient(to right, transparent, rgba(41,188,155,0.30), transparent)" }}
      />
    </section>
  );
}

// ───────────────────────── ESCENA 7 · COACH ESTRATÉGICO ───────────────────────── //
function CoachScene({ onCta }) {
  const [ref, visible] = useReveal(0.2);
  return (
    <section
      ref={ref}
      data-testid="coach-section"
      className="relative min-h-screen flex items-center px-6 py-32 md:py-40 overflow-hidden"
    >
      {/* Atmospheric layers */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-1/4 left-[-15%] w-[1000px] h-[1000px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(41,188,155,0.08) 0%, transparent 55%)",
          filter: "blur(80px)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-[-25%] right-[-10%] w-[900px] h-[900px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(26,35,48,0.6) 0%, transparent 60%)",
          filter: "blur(80px)",
        }}
      />

      <div className="relative w-full max-w-[1280px] mx-auto">
        {/* TOP — Eyebrow + Headline (centered/left) */}
        <div className="max-w-[1080px]">
          <div
            className={`text-[11px] tracking-[0.4em] uppercase text-[#29BC9B] font-semibold mb-10 transition-all duration-1000 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Coach estratégico
          </div>

          <h2
            data-testid="coach-title"
            className={`font-semibold leading-[1] tracking-[-0.025em] text-[40px] sm:text-[58px] md:text-[74px] lg:text-[86px] transition-all duration-1000 delay-150 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            No basta con <span className="text-white/55">conocerte.</span>
            <br />
            Necesitas{" "}
            <span className="relative inline-block">
              <span className="relative z-[1]">entrenarte.</span>
              <span
                aria-hidden
                className="absolute inset-x-0 bottom-2 h-[14px] bg-[#29BC9B]/25 -z-0"
              />
            </span>
          </h2>
        </div>

        {/* CENTER — Coach UI mockup (hero product shot) */}
        <div
          className={`relative mt-20 md:mt-24 transition-all duration-1000 delay-300 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          {/* Subtle teal glow behind the screenshot */}
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-x-10 -top-10 bottom-0"
            style={{
              background:
                "radial-gradient(ellipse at 50% 30%, rgba(41,188,155,0.18) 0%, transparent 60%)",
              filter: "blur(60px)",
            }}
          />

          {/* Device frame */}
          <div
            className="relative mx-auto max-w-[1140px] rounded-[18px] overflow-hidden border border-white/[0.08]"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)",
              boxShadow:
                "0 1px 0 rgba(255,255,255,0.05) inset, 0 30px 80px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(41,188,155,0.08)",
            }}
          >
            {/* Top window chrome (subtle) */}
            <div className="flex items-center gap-1.5 px-5 py-3 border-b border-white/[0.06] bg-black/40">
              <div className="w-2.5 h-2.5 rounded-full bg-white/15" />
              <div className="w-2.5 h-2.5 rounded-full bg-white/15" />
              <div className="w-2.5 h-2.5 rounded-full bg-white/15" />
              <div className="ml-4 text-[10px] tracking-[0.22em] uppercase text-white/35 font-semibold">
                play your game™ · coach
              </div>
            </div>

            {/* The actual Coach screenshot */}
            <img
              src="https://customer-assets.emergentagent.com/job_strategic-evolution/artifacts/nycjavqy_Coach%20leon.png"
              alt="Interfaz del Coach Estratégico — sesión Coach León"
              className="w-full h-auto block"
              loading="lazy"
            />

            {/* Bottom inner highlight */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-[18px]"
              style={{
                boxShadow: "inset 0 -1px 0 rgba(255,255,255,0.04)",
              }}
            />
          </div>

          {/* Caption beneath the mockup */}
          <div className="mt-6 text-center text-[10px] tracking-[0.32em] uppercase text-white/35 font-semibold">
            Sesión real · Coach León · 3 minutos para ganar claridad
          </div>
        </div>

        {/* BOTTOM — Body + CTA */}
        <div className="mt-20 md:mt-24 grid grid-cols-12 gap-10 items-end">
          <p
            className={`col-span-12 lg:col-span-7 text-[17px] sm:text-[19px] leading-relaxed text-white/60 max-w-[640px] transition-all duration-1000 delay-500 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Tu Coach Estratégico te acompaña con conversaciones guiadas para
            que aprendas a regular tu modo sombra y desarrollar madurez
            estratégica bajo presión.
          </p>

          <div className="col-span-12 lg:col-span-5 lg:text-right">
            <button
              onClick={onCta}
              data-testid="coach-cta-btn"
              className={`group inline-flex items-center gap-3 bg-[#29BC9B] hover:bg-[#22a989] text-[#050505] text-[12px] tracking-[0.22em] uppercase font-bold px-7 py-4 rounded-sm transition-all duration-500 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
              style={{ transitionDelay: "650ms" }}
            >
              Descubre cómo juegas bajo presión
              <ArrowRight
                size={15}
                className="group-hover:translate-x-1 transition-transform"
              />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ───────────────────────── ESCENA 8 · EMPRESAS (BAND PUENTE) ───────────────────────── //
function EmpresasScene() {
  const [ref, visible] = useReveal(0.15);
  const capabilities = [
    {
      label: "01",
      title: "Diagnóstico cultural",
      copy: "Cómo juega tu organización bajo presión, mapeado por rol, equipo y unidad.",
    },
    {
      label: "02",
      title: "Programa por rol",
      copy: "Rutas de evolución diseñadas para líderes, comerciales, producto, operaciones.",
    },
    {
      label: "03",
      title: "Coach estratégico a escala",
      copy: "IA conversacional + acompañamiento humano. Madurez bajo presión, sin fricción.",
    },
  ];

  return (
    <section
      ref={ref}
      data-testid="empresas-section"
      className="relative min-h-screen flex items-center px-6 py-32 md:py-40 overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, #050505 0%, #0E1218 50%, #050505 100%)",
      }}
    >
      {/* Atmospheric blue/teal accents */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-1/3 right-[-15%] w-[900px] h-[900px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(56,90,140,0.18) 0%, transparent 60%)",
          filter: "blur(80px)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-[10%] left-[-10%] w-[700px] h-[700px] rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(41,188,155,0.10) 0%, transparent 60%)",
          filter: "blur(80px)",
        }}
      />

      {/* Team-in-conversation atmospheric layer — same cinematic treatment
          as Problema/Ventaja but with a multi-face team scene. Anchored
          center-right so the headline on the left stays clean. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden"
      >
        <img
          src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=2000&q=85"
          alt=""
          className="ly2-face-img absolute inset-0 w-full h-full object-cover"
          style={{
            filter: "grayscale(1) contrast(1.10) brightness(0.98)",
            mixBlendMode: "screen",
            opacity: 0.38,
            objectPosition: "center 45%",
            transform: "translateX(8%) scale(1.0)",
            WebkitMaskImage:
              "radial-gradient(ellipse 48% 45% at 72% 38%, #000 22%, rgba(0,0,0,0.50) 52%, transparent 82%)",
            maskImage:
              "radial-gradient(ellipse 48% 45% at 72% 38%, #000 22%, rgba(0,0,0,0.50) 52%, transparent 82%)",
          }}
        />
        {/* Dark reading veil — protects the headline (left) */}
        <div
          className="ly2-reading-veil-dark absolute inset-0"
          style={{
            background:
              "linear-gradient(95deg, rgba(7,10,14,0.92) 0%, rgba(7,10,14,0.55) 38%, rgba(7,10,14,0.10) 65%, rgba(7,10,14,0) 100%)",
          }}
        />
        {/* Bottom veil — keeps capability cards clean above the team layer */}
        <div
          className="absolute inset-x-0 bottom-0 h-[55%]"
          style={{
            background:
              "linear-gradient(to bottom, rgba(7,10,14,0) 0%, rgba(7,10,14,0.70) 35%, rgba(7,10,14,0.92) 65%, rgba(7,10,14,0.98) 100%)",
          }}
        />
      </div>

      {/* Top & bottom subtle separators */}
      <div
        aria-hidden
        className="absolute top-0 inset-x-0 h-px"
        style={{
          background:
            "linear-gradient(to right, transparent, rgba(255,255,255,0.10), transparent)",
        }}
      />
      <div
        aria-hidden
        className="absolute bottom-0 inset-x-0 h-px"
        style={{
          background:
            "linear-gradient(to right, transparent, rgba(255,255,255,0.10), transparent)",
        }}
      />

      <div className="relative z-10 w-full max-w-[1180px] mx-auto">
        {/* Header row */}
        <div className="grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 lg:col-span-7">
            <div
              className={`text-[11px] tracking-[0.4em] uppercase text-[#29BC9B] font-semibold mb-10 transition-all duration-1000 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
            >
              Para equipos · Empresas
            </div>

            <h2
              data-testid="empresas-title"
              className={`font-semibold leading-[1.02] tracking-[-0.025em] text-[40px] sm:text-[54px] md:text-[66px] transition-all duration-1000 delay-150 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
            >
              Cuando un equipo entero <br />
              <span className="text-white/55">
                entiende cómo juega bajo presión,
              </span>
              <br />
              la conversación cambia.
            </h2>
          </div>

          <div className="col-span-12 lg:col-span-5 lg:text-right">
            <p
              className={`text-[15px] leading-relaxed text-white/55 max-w-[420px] lg:ml-auto transition-all duration-1000 delay-300 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
            >
              Play Your Game™ se implementa en empresas que buscan claridad
              estratégica en sus líderes, no más diagnósticos.
            </p>
          </div>
        </div>

        {/* Capabilities grid */}
        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.05] border border-white/[0.05] rounded-md overflow-hidden">
          {capabilities.map((c, i) => (
            <div
              key={c.label}
              data-testid={`empresas-cap-${i + 1}`}
              className="group relative bg-[#070A0E] p-10 md:p-12 transition-colors duration-500 hover:bg-[#0C1218]"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(20px)",
                transition: "opacity 1000ms, transform 1000ms",
                transitionDelay: `${450 + i * 120}ms`,
              }}
            >
              <div className="text-[10px] tracking-[0.32em] uppercase font-bold text-[#29BC9B]/70 mb-8">
                {c.label}
              </div>
              <div className="text-[22px] sm:text-[24px] font-semibold tracking-[-0.015em] leading-[1.15] text-white">
                {c.title}
              </div>
              <p className="mt-5 text-[14px] leading-relaxed text-white/55">
                {c.copy}
              </p>
              <div
                aria-hidden
                className="absolute bottom-0 left-0 h-px w-0 group-hover:w-full bg-[#29BC9B] transition-all duration-700"
              />
            </div>
          ))}
        </div>

        {/* CTA row */}
        <div
          className={`mt-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 transition-all duration-1000 delay-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <div className="text-[15px] text-white/45 max-w-[520px] leading-relaxed">
            Programas a medida, lanzamientos para 30 a 3.000 colaboradores,
            dashboards por unidad.
          </div>

          <Link
            to="/empresas"
            data-testid="empresas-cta-btn"
            className="group inline-flex items-center gap-3 border border-white/15 hover:border-[#29BC9B]/70 bg-white/[0.02] hover:bg-[#29BC9B]/[0.06] text-white text-[12px] tracking-[0.22em] uppercase font-semibold px-7 py-4 rounded-sm transition-all duration-500"
          >
            Hablar con el equipo
            <ArrowRight
              size={14}
              className="group-hover:translate-x-1 transition-transform"
            />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ───────────────────────── ESCENA 9 · CTA FINAL ───────────────────────── //
function CtaFinalScene({ onCta }) {
  const [ref, visible] = useReveal(0.25);

  return (
    <section
      ref={ref}
      data-testid="cta-final-section"
      className="relative min-h-[90vh] flex items-center justify-center px-6 py-40 overflow-hidden bg-[#050505]"
    >
      {/* Dramatic teal glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 50% 60%, rgba(41,188,155,0.20) 0%, transparent 60%)",
          filter: "blur(40px)",
        }}
      />
      {/* Vertical thin line spotlight from top */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-px h-32"
        style={{
          background:
            "linear-gradient(to bottom, transparent, rgba(41,188,155,0.6), transparent)",
        }}
      />

      <div className="relative w-full max-w-[1080px] mx-auto text-center">
        <div
          className={`text-[11px] tracking-[0.4em] uppercase text-[#29BC9B] font-semibold mb-12 transition-all duration-1000 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          Tu próximo movimiento
        </div>

        <h2
          data-testid="cta-final-title"
          className={`font-semibold leading-[1] tracking-[-0.03em] text-[44px] sm:text-[64px] md:text-[88px] lg:text-[108px] transition-all duration-1000 delay-150 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          ¿Cómo juegas
          <br />
          <span className="text-white/45">tú bajo presión?</span>
        </h2>

        <p
          className={`mt-12 text-[16px] sm:text-[19px] leading-relaxed text-white/50 max-w-[640px] mx-auto transition-all duration-1000 delay-300 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          24 preguntas. 6 minutos. Una respuesta estratégica que no esperabas
          sobre ti mismo.
        </p>

        <div
          className={`mt-14 transition-all duration-1000 delay-500 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <button
            onClick={onCta}
            data-testid="cta-final-btn"
            className="group inline-flex items-center gap-3 bg-[#29BC9B] hover:bg-[#22a989] text-[#050505] text-[12px] sm:text-[13px] tracking-[0.22em] uppercase font-bold px-9 py-5 rounded-sm transition-all duration-500 shadow-[0_8px_40px_-12px_rgba(41,188,155,0.6)]"
          >
            Empezar mi diagnóstico
            <ArrowRight
              size={16}
              className="group-hover:translate-x-1 transition-transform"
            />
          </button>
          <div className="mt-6 text-[10px] tracking-[0.28em] uppercase text-white/30 font-medium">
            Gratis · Sin registro previo · Resultado inmediato
          </div>
        </div>
      </div>
    </section>
  );
}

// ───────────────────────── ESCENA 10 · FOOTER ───────────────────────── //
function FooterScene() {
  return (
    <footer
      data-testid="footer-section"
      className="relative px-6 pt-24 pb-12 border-t border-white/[0.06] bg-[#050505]"
    >
      <div className="max-w-[1180px] mx-auto">
        <div className="grid grid-cols-12 gap-10">
          {/* Brand */}
          <div className="col-span-12 md:col-span-5">
            <div className="text-sm font-semibold tracking-[0.32em] text-white">
              PLAY YOUR GAME™
            </div>
            <p className="mt-6 text-[14px] leading-relaxed text-white/40 max-w-[360px]">
              Sistema estratégico de evolución personal. Diseñado para
              profesionales que toman decisiones bajo presión.
            </p>
            <div className="mt-8 text-[10px] tracking-[0.32em] uppercase text-[#29BC9B]/70 font-semibold">
              Juega mejor bajo presión.
            </div>
          </div>

          {/* Sistema */}
          <div className="col-span-6 md:col-span-3">
            <div className="text-[10px] tracking-[0.32em] uppercase text-white/35 font-semibold mb-6">
              Sistema
            </div>
            <ul className="space-y-3 text-[14px] text-white/60">
              <li>
                <a href="#problema" className="hover:text-white transition-colors">
                  Problema
                </a>
              </li>
              <li>
                <a href="#evolucion" className="hover:text-white transition-colors">
                  Evolución
                </a>
              </li>
              <li>
                <a href="#avatares" className="hover:text-white transition-colors">
                  Avatares
                </a>
              </li>
              <li>
                <a href="#coach-section" className="hover:text-white transition-colors">
                  Coach
                </a>
              </li>
            </ul>
          </div>

          {/* Acceso */}
          <div className="col-span-6 md:col-span-4">
            <div className="text-[10px] tracking-[0.32em] uppercase text-white/35 font-semibold mb-6">
              Acceso
            </div>
            <ul className="space-y-3 text-[14px] text-white/60">
              <li>
                <Link to="/acceder" className="hover:text-white transition-colors">
                  Centro de evolución
                </Link>
              </li>
              <li>
                <Link to="/empresas" className="hover:text-white transition-colors">
                  Empresas
                </Link>
              </li>
              <li>
                <a
                  href="mailto:hola@playyourgame.com"
                  className="hover:text-white transition-colors"
                >
                  hola@playyourgame.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom row */}
        <div className="mt-20 pt-8 border-t border-white/[0.05] flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-[11px] text-white/30 tracking-wide">
            © {new Date().getFullYear()} Play Your Game™. Todos los derechos
            reservados.
          </div>
          <div className="flex items-center gap-6 text-[10px] tracking-[0.22em] uppercase text-white/30 font-medium">
            <Link to="/privacidad" className="hover:text-white/60 transition-colors">
              Privacidad
            </Link>
            <Link to="/privacidad" className="hover:text-white/60 transition-colors">
              Términos
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
